/**
 * Cliente HTTP de la SPA: un único envoltorio de fetch tipado con los contratos de
 * services/web/src/shared/contracts.ts.
 *
 * - Mismo origen y cookie de sesión (HttpOnly): credentials "same-origin", nunca
 *   tokens en JS ni en localStorage.
 * - Errores RFC 9457 (application/problem+json) → ApiError con los errores por campo.
 * - 401 en cualquier llamada autenticada → el manejador global (registrado en
 *   main.tsx) limpia la caché y lleva a /login?next=…
 * - Nada de datos personales en URLs: las búsquedas viajan en el cuerpo del POST.
 */

import type {
  AgentCreate, AgentEnrollment, ScraperAgent, SealedSessionUpload, SocialAccount, SocialAccountCreate, SocialAccountPatch,
  AuditEntry, ChangePasswordRequest, Facets, Institution, InstitutionCreate, InstitutionUpdate, InviteLink, MonthlyUsage,
  IntelView, ListingDetail, LiveState, LoginRequest, Me, Page, Problem, SearchHistoryPage, SearchPage, SearchRecord,
  SearchRequest, SearchResponse, SearchSummary, SourceYieldReport, SystemStatus, Target, TargetBulk, TargetCategoria, TargetCreate, TargetPatch,
  TokenInspectResponse, TokenRedeemRequest, UsageRow, User, UserCreate, UserUpdate,
  ListingPhotoMatches, LensRequest, LensResponse, PhotoMatchSummary, PhotoStatus, PhotoUploadRequest, PhotoUploadResponse,
} from "../shared/contracts.js";

// ─── Tipos de respuestas que contracts.ts no fija (forma acordada en el diseño §6) ──

/** GET /api/auth/sessions */
export interface SessionInfo {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  ip: string | null;
  userAgent: string | null;
  current: boolean;
}

/** POST /api/admin/users → el enlace de invitación se muestra una sola vez. */
export interface UserCreated {
  user: User;
  invite: InviteLink;
}

export type InstitutionStatusFilter = Institution["status"];
export type UsageGroupBy = "institution" | "user" | "day";

export interface InstitutionListParams { status?: InstitutionStatusFilter; q?: string; cursor?: string | null }
export interface UserListParams {
  institutionId?: string;
  role?: User["role"];
  status?: User["status"];
  q?: string;
  cursor?: string | null;
}
export interface AuditParams {
  institutionId?: string;
  userId?: string;
  action?: string;
  /** YYYY-MM-DD (inclusive). */
  from?: string;
  to?: string;
  cursor?: string | null;
}
export interface UsageParams { groupBy: UsageGroupBy; from?: string; to?: string }

export interface TargetListParams { categoria?: TargetCategoria; activo?: boolean }

// ─── Errores ────────────────────────────────────────────────────────────────

const STATUS_TITLES: Record<number, string> = {
  0: "No se pudo conectar con el servidor. Revisa tu conexión e intenta nuevamente.",
  400: "Hay datos inválidos en la solicitud.",
  401: "Tu sesión expiró. Ingresa nuevamente.",
  403: "No tienes permiso para realizar esta acción.",
  404: "No encontramos lo que buscabas.",
  409: "La operación entra en conflicto con datos existentes.",
  413: "La solicitud es demasiado grande.",
  415: "Formato de solicitud no admitido.",
  422: "No se pudo completar la operación.",
  429: "Demasiadas solicitudes. Espera un momento antes de reintentar.",
};

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;
  /** Segundos indicados por Retry-After (429), si vinieron. */
  readonly retryAfterSeconds: number | null;

  constructor(problem: Problem, retryAfterSeconds: number | null = null) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.status = problem.status;
    this.problem = problem;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** Errores por campo ({ "context.purpose": "…" }) para pintarlos junto a cada input. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) {
      if (!(e.path in out)) out[e.path] = e.message;
    }
    return out;
  }
}

function fallbackTitle(status: number): string {
  return STATUS_TITLES[status] ?? (status >= 500 ? "Error del servidor. Intenta nuevamente en unos minutos." : `Error ${status}`);
}

/** Mensaje legible para el usuario a partir de cualquier error. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.problem.detail ?? err.problem.title;
  return fallbackTitle(0);
}

export function isApiError(err: unknown, status?: number): err is ApiError {
  return err instanceof ApiError && (status === undefined || err.status === status);
}

/** Retry-After admite segundos o fecha HTTP. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

// ─── Redirección por sesión vencida ─────────────────────────────────────────

let unauthorizedHandler: (() => void) | null = null;

/** main.tsx registra aquí qué hacer ante un 401 (limpiar caché y navegar a /login). */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

/**
 * Destino post-login seguro: solo rutas internas absolutas. Evita redirecciones
 * abiertas (`//evil.com`, `/\evil.com`, `https://…`) a través de ?next=.
 */
export function safeNext(next: string | null | undefined, fallback = "/buscar"): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) return fallback;
  if (next === "/login" || next.startsWith("/login?") || next.startsWith("/set-password")) return fallback;
  return next;
}

// ─── Envoltorio de fetch ────────────────────────────────────────────────────

type QueryValue = string | number | boolean | null | undefined;

/** Agrega parámetros de consulta omitiendo vacíos (nunca se usa para datos personales). */
export function withQuery(path: string, params?: Record<string, QueryValue>): string {
  if (!params) return path;
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `${path}?${qs}` : path;
}

interface RequestOptions {
  body?: unknown;
  signal?: AbortSignal;
  /** false en login/me/tokens: un 401 ahí es una respuesta esperada, no "sesión vencida". */
  redirectOn401?: boolean;
}

async function readProblem(res: Response): Promise<Problem> {
  const type = res.headers.get("content-type") ?? "";
  if (type.includes("json")) {
    try {
      const body = (await res.json()) as Partial<Problem>;
      return {
        type: body.type ?? "about:blank",
        title: body.title ?? fallbackTitle(res.status),
        status: res.status,
        ...(body.detail ? { detail: body.detail } : {}),
        ...(Array.isArray(body.errors) ? { errors: body.errors } : {}),
      };
    } catch {
      // Cuerpo corrupto: se cae al título genérico de abajo.
    }
  }
  return { type: "about:blank", title: fallbackTitle(res.status), status: res.status };
}

export async function request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body, credentials: "same-origin", signal: opts.signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError({ type: "about:blank", title: fallbackTitle(0), status: 0 });
  }
  if (!res.ok) {
    const problem = await readProblem(res);
    if (res.status === 401 && opts.redirectOn401 !== false) unauthorizedHandler?.();
    throw new ApiError(problem, parseRetryAfter(res.headers.get("retry-after")));
  }
  if (res.status === 204 || res.headers.get("content-length") === "0") return undefined as T;
  return (await res.json()) as T;
}

const get = <T>(path: string, signal?: AbortSignal, redirectOn401 = true) =>
  request<T>("GET", path, { signal, redirectOn401 });
const post = <T>(path: string, body?: unknown, redirectOn401 = true) =>
  request<T>("POST", path, { body: body ?? {}, redirectOn401 });
const patch = <T>(path: string, body: unknown) => request<T>("PATCH", path, { body });
const put = <T>(path: string, body: unknown) => request<T>("PUT", path, { body });
const del = <T>(path: string) => request<T>("DELETE", path);

const enc = encodeURIComponent;

// ─── Endpoints ──────────────────────────────────────────────────────────────

export const api = {
  auth: {
    login: (body: LoginRequest) => post<Me>("/api/auth/login", body, false),
    logout: () => post<void>("/api/auth/logout", undefined, false),
    me: (signal?: AbortSignal) => get<Me>("/api/auth/me", signal, false),
    changePassword: (body: ChangePasswordRequest) => post<void>("/api/auth/password", body),
    inspectToken: (token: string) => post<TokenInspectResponse>("/api/auth/token/inspect", { token }, false),
    redeemToken: (body: TokenRedeemRequest) => post<void>("/api/auth/token/redeem", body, false),
    sessions: (signal?: AbortSignal) => get<{ items: SessionInfo[] }>("/api/auth/sessions", signal),
    revokeSession: (id: string) => del<void>(`/api/auth/sessions/${enc(id)}`),
  },
  searches: {
    create: (body: SearchRequest) => post<SearchResponse>("/api/searches", body),
    get: (id: string, signal?: AbortSignal) => get<SearchRecord>(`/api/searches/${enc(id)}`, signal),
    results: (id: string, cursor: string | null, signal?: AbortSignal) =>
      get<SearchPage>(withQuery(`/api/searches/${enc(id)}/results`, { cursor }), signal),
    history: (scope: "mine" | "institution", cursor: string | null, signal?: AbortSignal) =>
      get<SearchHistoryPage>(withQuery("/api/searches", { scope, cursor }), signal),
    /** Sondeo del estado en vivo (alternativa al SSE). `url` viene de SearchResponse.live.pollUrl. */
    live: (url: string, signal?: AbortSignal) => get<LiveState>(url, signal),
    /** Resumen con IA (crea o devuelve el guardado; `pending` = volver a pedir). */
    summary: (id: string) => post<SearchSummary>(`/api/searches/${enc(id)}/summary`),
    /** Análisis profundo: páginas leídas completas, puntaje contra el caso y pistas. */
    /** «Actualizar» la búsqueda en internet sin caché (202: se sigue con `live`). */
    refreshLive: (id: string) => post<{ status: string; streamUrl: string; pollUrl: string }>(`/api/searches/${enc(id)}/live/refresh`),
    intel: (id: string, signal?: AbortSignal) => get<IntelView>(`/api/searches/${enc(id)}/intel`, signal),
    pivot: (id: string, pivotId: string, action: "approve" | "dismiss") =>
      post<void>(`/api/searches/${enc(id)}/intel/pivots/${enc(pivotId)}/${action}`),
  },
  listings: {
    get: (id: string, signal?: AbortSignal) => get<ListingDetail>(`/api/listings/${enc(id)}`, signal),
    /** Otros avisos con la misma foto que este. */
    photoMatches: (id: string, signal?: AbortSignal) =>
      get<ListingPhotoMatches>(`/api/listings/${enc(id)}/photo-matches`, signal),
    /** Lo mismo para los avisos de un caso (máx. 60; ids de avisos, no datos personales). */
    photoMatchSummary: (ids: string[], signal?: AbortSignal) =>
      get<PhotoMatchSummary>(withQuery("/api/listings/photo-matches", { ids: ids.join(",") }), signal),
  },
  photos: {
    status: (signal?: AbortSignal) => get<PhotoStatus>("/api/photos/status", signal),
    upload: (body: PhotoUploadRequest) => post<PhotoUploadResponse>("/api/photos/upload", body),
    lens: (body: LensRequest) => post<LensResponse>("/api/photos/lens", body),
  },
  snapshots: {
    raw: (id: string, signal?: AbortSignal) => get<unknown>(`/api/snapshots/${enc(id)}/raw`, signal),
  },
  meta: {
    facets: (signal?: AbortSignal) => get<Facets>("/api/meta/facets", signal),
  },
  admin: {
    institutions: (p: InstitutionListParams, signal?: AbortSignal) =>
      get<Page<Institution>>(withQuery("/api/admin/institutions", { status: p.status, q: p.q, cursor: p.cursor }), signal),
    institution: (id: string, signal?: AbortSignal) => get<Institution>(`/api/admin/institutions/${enc(id)}`, signal),
    createInstitution: (body: InstitutionCreate) => post<Institution>("/api/admin/institutions", body),
    updateInstitution: (id: string, body: InstitutionUpdate) => patch<Institution>(`/api/admin/institutions/${enc(id)}`, body),
    users: (p: UserListParams, signal?: AbortSignal) =>
      get<Page<User>>(withQuery("/api/admin/users", {
        institutionId: p.institutionId, role: p.role, status: p.status, q: p.q, cursor: p.cursor,
      }), signal),
    createUser: (body: UserCreate) => post<UserCreated>("/api/admin/users", body),
    updateUser: (id: string, body: UserUpdate) => patch<User>(`/api/admin/users/${enc(id)}`, body),
    resetPassword: (id: string) => post<InviteLink>(`/api/admin/users/${enc(id)}/password-reset`),
    unlockUser: (id: string) => post<void>(`/api/admin/users/${enc(id)}/unlock`),
    revokeUserSessions: (id: string) => post<void>(`/api/admin/users/${enc(id)}/sessions/revoke`),
    audit: (p: AuditParams, signal?: AbortSignal) => get<Page<AuditEntry>>(auditUrl("/api/admin/audit", p), signal),
    usage: (p: UsageParams, signal?: AbortSignal) =>
      get<{ rows: UsageRow[] }>(withQuery("/api/admin/usage", { groupBy: p.groupBy, from: p.from, to: p.to }), signal),
    usageMonthly: (month: string, signal?: AbortSignal) =>
      get<MonthlyUsage>(withQuery("/api/admin/usage/monthly", { month }), signal),
    system: (signal?: AbortSignal) => get<SystemStatus>("/api/admin/system", signal),
    targets: (p: TargetListParams = {}, signal?: AbortSignal) =>
      get<{ items: Target[] }>(withQuery("/api/admin/targets", { categoria: p.categoria, activo: p.activo }), signal),
    createTarget: (body: TargetCreate) => post<Target>("/api/admin/targets", body),
    updateTarget: (id: string, body: TargetPatch) => patch<Target>(`/api/admin/targets/${enc(id)}`, body),
    deleteTarget: (id: string) => del<void>(`/api/admin/targets/${enc(id)}`),
    bulkTargets: (body: TargetBulk) => post<{ updated: number }>("/api/admin/targets/bulk", body),
    /** Rendimiento por fuente (se calcula cada 10 min; refresh = recalcular ahora). */
    sourceYield: (refresh = false, signal?: AbortSignal) =>
      get<SourceYieldReport>(withQuery("/api/admin/source-yield", { refresh: refresh ? "1" : undefined }), signal),
    agents: (signal?: AbortSignal) => get<{ items: ScraperAgent[] }>("/api/admin/agents", signal),
    createAgent: (body: AgentCreate) => post<AgentEnrollment>("/api/admin/agents", body),
    revokeAgent: (id: string) => post<ScraperAgent>(`/api/admin/agents/${enc(id)}/revoke`),
    runAgentNow: (id: string) => post<ScraperAgent>(`/api/admin/agents/${enc(id)}/run-now`),
    socialAccounts: (signal?: AbortSignal) => get<{ items: SocialAccount[] }>("/api/admin/social-accounts", signal),
    createSocialAccount: (body: SocialAccountCreate) => post<SocialAccount>("/api/admin/social-accounts", body),
    updateSocialAccount: (id: string, body: SocialAccountPatch) =>
      patch<SocialAccount>(`/api/admin/social-accounts/${enc(id)}`, body),
    deleteSocialAccount: (id: string) => del<void>(`/api/admin/social-accounts/${enc(id)}`),
    uploadAccountSession: (id: string, body: SealedSessionUpload) =>
      put<SocialAccount>(`/api/admin/social-accounts/${enc(id)}/session`, body),
    requestAccountLogin: (id: string) => post<SocialAccount>(`/api/admin/social-accounts/${enc(id)}/login`),
    forgetAccountSession: (id: string) => post<SocialAccount>(`/api/admin/social-accounts/${enc(id)}/forget-session`),
  },
};

function auditUrl(base: string, p: AuditParams): string {
  return withQuery(base, {
    institutionId: p.institutionId, userId: p.userId, action: p.action, from: p.from, to: p.to, cursor: p.cursor,
  });
}

/** Enlace de descarga del CSV de auditoría con los mismos filtros de la tabla. */
export function auditCsvUrl(p: Omit<AuditParams, "cursor">): string {
  return auditUrl("/api/admin/audit.csv", p);
}

// ─── URLs de recursos binarios (img/iframe; mismo origen, la cookie viaja sola) ──

export function screenshotUrl(snapshotId: string, size: "thumb" | "full", version?: string): string {
  return withQuery(`/api/snapshots/${enc(snapshotId)}/screenshot`, { size, v: version ?? snapshotId });
}
/** Foto almacenada n (base 0) de una captura. */
export function photoUrl(snapshotId: string, n: number, size?: "thumb"): string {
  return withQuery(`/api/snapshots/${enc(snapshotId)}/photos/${n}`, { size });
}
export function rawPreviewUrl(snapshotId: string): string {
  return `/api/snapshots/${enc(snapshotId)}/raw/preview`;
}
export function detailUrl(snapshotId: string, format?: "source"): string {
  return withQuery(`/api/snapshots/${enc(snapshotId)}/detail`, { format });
}
